import { useState } from "preact/hooks";
import type { CalculatorInputs, CalculatorResults, PersonInputs } from "../engine";
import { run } from "../engine";
import { Form, type FormState } from "./Form";
import { Results } from "./Results";

const DEFAULT_COLA = 0.025;
const DEFAULT_LONGEVITY = 85;

const initialPerson = {
  dob: "",
  benefitMode: "pia" as const,
  pia: "",
  aime: "",
  longevityAge: DEFAULT_LONGEVITY,
  claimAge: 67,
};

const initialFormState: FormState = {
  household: "single",
  primary: { ...initialPerson },
  spouse: { ...initialPerson },
  colaRate: DEFAULT_COLA,
};

function toPersonInputs(
  p: FormState["primary"]
): PersonInputs | { error: string } {
  if (!p.dob) return { error: "Please enter a date of birth." };
  const year = new Date(p.dob).getUTCFullYear();
  if (Number.isNaN(year) || year < 1900 || year > 2010) {
    return { error: "Date of birth looks out of range." };
  }
  const longevity = Number(p.longevityAge);
  if (!Number.isFinite(longevity) || longevity < 62 || longevity > 110) {
    return { error: "Longevity must be between 62 and 110." };
  }
  const claim = Number(p.claimAge);
  if (!Number.isFinite(claim) || claim < 62 || claim > 70) {
    return { error: "Claim age must be between 62 and 70." };
  }
  const input: PersonInputs = {
    dob: p.dob,
    longevityAge: longevity,
    claimAge: claim,
  };
  if (p.benefitMode === "pia") {
    const pia = Number(p.pia);
    if (!Number.isFinite(pia) || pia <= 0) {
      return { error: "Enter a Primary Insurance Amount greater than zero." };
    }
    input.pia = pia;
  } else {
    const aime = Number(p.aime);
    if (!Number.isFinite(aime) || aime <= 0) {
      return { error: "Enter an AIME greater than zero." };
    }
    input.aime = aime;
  }
  return input;
}

interface ResultsSnapshot {
  results: CalculatorResults;
  inputs: CalculatorInputs;
  household: "single" | "couple";
  primaryLongevity: number;
  spouseLongevity: number;
}

export function App() {
  const [form, setForm] = useState<FormState>(initialFormState);
  const [snapshot, setSnapshot] = useState<ResultsSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);

  function handleSubmit() {
    setError(null);
    const primary = toPersonInputs(form.primary);
    if ("error" in primary) {
      setError(primary.error);
      setSnapshot(null);
      return;
    }
    let spouse: PersonInputs | undefined;
    if (form.household === "couple") {
      const sp = toPersonInputs(form.spouse);
      if ("error" in sp) {
        setError(`Spouse: ${sp.error}`);
        setSnapshot(null);
        return;
      }
      spouse = sp;
    }
    const inputs: CalculatorInputs = {
      primary,
      spouse,
      colaRate: form.colaRate,
    };
    try {
      const results = run(inputs);
      setSnapshot({
        results,
        inputs,
        household: form.household,
        primaryLongevity: primary.longevityAge,
        spouseLongevity: spouse?.longevityAge ?? DEFAULT_LONGEVITY,
      });
      queueMicrotask(() => {
        const el = document.getElementById("tca-ss-results");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Calculation error.");
      setSnapshot(null);
    }
  }

  function handleReset() {
    setForm(initialFormState);
    setSnapshot(null);
    setError(null);
  }

  return (
    <div class="tca-ss-calc">
      <header class="tca-ss-calc__intro">
        <h2>Social Security Timing Calculator</h2>
        <p class="tca-ss-calc__lede">
          See how your claim age changes your monthly benefit and your lifetime
          income. All math runs in your browser — nothing you enter is sent to
          us.
        </p>
        <p class="tca-ss-calc__sourcing">
          Built on the SSA's published formulas — full Retirement Age table at{" "}
          <a
            href="https://www.ssa.gov/oact/progdata/nra.html"
            target="_blank"
            rel="noopener noreferrer"
          >
            ssa.gov/oact/progdata/nra.html
          </a>
          , bend points at{" "}
          <a
            href="https://www.ssa.gov/oact/cola/bendpoints.html"
            target="_blank"
            rel="noopener noreferrer"
          >
            ssa.gov/oact/cola/bendpoints.html
          </a>
          , COLA history at{" "}
          <a
            href="https://www.ssa.gov/oact/cola/colaseries.html"
            target="_blank"
            rel="noopener noreferrer"
          >
            ssa.gov/oact/cola/colaseries.html
          </a>
          .
        </p>
      </header>

      <Form
        state={form}
        onChange={setForm}
        onSubmit={handleSubmit}
        onReset={handleReset}
        error={error}
      />

      {snapshot && (
        <div id="tca-ss-results">
          <Results
            results={snapshot.results}
            household={snapshot.household}
            colaRate={snapshot.inputs.colaRate}
            primaryLongevity={snapshot.primaryLongevity}
            spouseLongevity={snapshot.spouseLongevity}
          />
        </div>
      )}
    </div>
  );
}
